import { useCallback, useEffect, useRef, useState } from 'react';

const useDropdown = <T extends HTMLElement>() => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<T>(null);

  /**
   * Controls closing the dropdown when clicking outside of it.
   * @param {MouseEvent | TouchEvent} event - The triggered event.
   */
  const handleClickOutside = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!dropdownRef?.current) {
        return;
      }

      if (dropdownRef.current.contains(event.target as Node)) {
        return;
      }

      if (isOpen) {
        event.stopImmediatePropagation();
        event.stopPropagation();
        event.preventDefault();

        setIsOpen(false);
      }
    },
    [isOpen]
  );
  /**
   * Listens for clicks outside of the dropdown element to close it
   * and stops propagation to prevent other click handlers from firing.
   * Events to listen: touchend, click (touchable devices and desktops).
   */
  useEffect(() => {
    document.addEventListener('touchend', handleClickOutside, true);
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('touchend', handleClickOutside, true);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [handleClickOutside]);

  const closeDropdown = () => {
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(prev => {
      return !prev;
    });
  };

  return { isOpen, setIsOpen, dropdownRef, closeDropdown, toggleDropdown };
};

export default useDropdown;
